import React, { useEffect, useState, useRef } from "react";
import Button from "../elements/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { getShortPlayerName } from "../pages/Duel";
import TeamLogo from "./TeamLogo";
import { getTeamName, isLiveMatches } from "../utils";
import CloseIcon from "@mui/icons-material/Close";
import API from "../api";

const getPenaltiesScore = (roundMatches = []) => {
  const calculatedAdditionalMatches = roundMatches.filter(
    (match) => match.IsAdditional && match.Calculated
  );
  const firstPlayerAdditionalPts = calculatedAdditionalMatches.reduce(
    (acc, curr) => acc + curr.firstPlayerPrediction.Pts,
    0
  );
  const secondPlayerAdditionalPts = calculatedAdditionalMatches.reduce(
    (acc, curr) => acc + curr.secondPlayerPrediction.Pts,
    0
  );

  return `${firstPlayerAdditionalPts}:${secondPlayerAdditionalPts}`;
};

const isAdditionalVisible = (roundMatches = []) => {
  const calculatedMainMatches = roundMatches.filter(
    (match) => !match.IsAdditional && match.Calculated
  );
  const firstPlayerMainPts = calculatedMainMatches.reduce(
    (acc, curr) => acc + (curr.firstPlayerPrediction?.Pts || 0),
    0
  );
  const secondPlayerMainPts = calculatedMainMatches.reduce(
    (acc, curr) => acc + (curr.secondPlayerPrediction?.Pts || 0),
    0
  );

  const isAllMatchesCalculated =
    calculatedMainMatches.length ===
    roundMatches.filter((match) => !match.IsAdditional).length;

  return isAllMatchesCalculated && firstPlayerMainPts === secondPlayerMainPts;
};

const getPlayerPrediction = (matchPrediction, matchPtsClass) => {
  if (matchPrediction) {
    return (
      <>
        {matchPrediction.FirstScore}:{matchPrediction.SecondScore}
        <span
          className={`absolute -top-2.5 text-sm font-bold ${
            matchPrediction.Calculated ? "text-green" : "text-red animate-live"
          } ${matchPtsClass}`}
        >
          {matchPrediction.Pts ? `+${matchPrediction.Pts}` : ``}
        </span>
      </>
    );
  } else {
    return "-";
  }
};

export default function DuelDialog({
  open,
  handleClose,
  initialDuelInfo,
  teams,
  matches,
  autoRefreshInterval,
}) {
  const [duelState, setDuelState] = useState({});

  useEffect(() => {
    setDuelState(initialDuelInfo);
  }, [initialDuelInfo]);

  const refreshDuel = async (duelId) => {
    try {
      const response = await API.fetchDuel(duelId);
      setDuelState(response.data);
    } catch (err) {
      console.log(err);
    }
  };

  const duelStateRef = useRef(duelState);
  const matchesRef = useRef(matches);
  const openRef = useRef(open);
  const intervalRef = useRef(autoRefreshInterval);

  useEffect(() => {
    duelStateRef.current = duelState;
    matchesRef.current = matches;
    openRef.current = open;
    intervalRef.current = autoRefreshInterval;
  }, [duelState, matches, open, autoRefreshInterval]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (
        !isLiveMatches(matchesRef.current) ||
        !duelStateRef.current.duelId ||
        !openRef.current ||
        !intervalRef.current
      )
        return;

      refreshDuel(duelStateRef.current.duelId);
    }, intervalRef.current * 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      if (
        !isLiveMatches(matches) ||
        !duelState.duelId ||
        !open ||
        !autoRefreshInterval
      )
        return;
      refreshDuel(duelState.duelId);
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [duelState, matches, open, autoRefreshInterval]);

  return (
    <Dialog
      className="!font-sans"
      open={open}
      onClose={handleClose}
      aria-labelledby="responsive-dialog-title"
      maxWidth={"sm"}
      fullWidth={true}
    >
      <DialogTitle
        id="responsive-dialog-title"
        className="!font-sans flex text-base font-bold items-center text-white bg-cyan/50 uppercase"
      >
        <div className="flex items-center justify-between w-full md:min-w-[460px] bg-primary rounded-[150px_150px_150px_150px/150px_150px_150px_150px]">
          <div className="flex justify-start items-center flex-[2_1_50%] relative text-base md:text-xl bg-gradient-to-r from-primaryLight rounded-[150px_150px_150px_150px/150px_150px_150px_150px]">
            <img
              src={duelState?.duel?.FirstPlayer?.Logo}
              alt=""
              className="h-10 w-10 rounded-full object-cover"
            />

            <span className="hidden w-48 truncate md:block text-sm md:text-xl font-bold text-left ml-2">
              {getShortPlayerName(duelState?.duel?.FirstPlayer?.FullName)}
            </span>
          </div>
          <div className="text-center flex-initial text-2xl font-bold min-w-9">
            {duelState?.duel?.FirstPlayerScore}:
            {duelState?.duel?.SecondPlayerScore}
          </div>
          <div className="flex justify-end items-center flex-[2_1_50%] relative bg-gradient-to-l from-primaryLight rounded-[150px_150px_150px_150px/150px_150px_150px_150px]">
            <span className="hidden w-48 truncate md:block mr-2 text-sm md:text-xl font-bold text-right">
              {getShortPlayerName(duelState?.duel?.SecondPlayer?.FullName)}
            </span>
            <img
              src={duelState?.duel?.SecondPlayer?.Logo}
              alt=""
              className="h-10 w-10 rounded-full object-cover"
            />
          </div>
        </div>
      </DialogTitle>
      <DialogContent className="from-cyan/50 to-lightblue/50 bg-gradient-to-b">
        <DialogContentText className="text-black !font-sans">
          {duelState?.roundMatches
            ?.filter((match) => !match.IsAdditional)
            .map((match) => (
              <div
                key={match.MatchId}
                className="flex items-center justify-around px-0 py-2 text-black"
              >
                <div className="justify-start w-8 flex items-center flex-[0_1_90px] relative">
                  {getPlayerPrediction(match.firstPlayerPrediction, "-left-4")}
                </div>
                <div className="flex justify-center items-center flex-[0_1_80px]">
                  <TeamLogo teamId={match.FirstTeam} isFirstTeam />
                  <span className="w-8 ml-2">
                    {getTeamName(match.FirstTeam, teams, true)}
                  </span>
                </div>
                <div
                  className={`text-center font-bold w-24 ${
                    match.Calculated ||
                    (match.FirstScore === undefined &&
                      match.SecondScore === undefined)
                      ? "text-primary"
                      : "text-red animate-live"
                  }`}
                >
                  {match.FirstScore}:{match.SecondScore}
                </div>
                <div className="flex justify-center items-center flex-[0_1_80px]">
                  <span className="w-8 mr-2">
                    {getTeamName(match.SecondTeam, teams, true)}
                  </span>
                  <TeamLogo teamId={match.SecondTeam} />
                </div>
                <div className="justify-end w-8 flex items-center flex-[0_1_90px] relative">
                  {getPlayerPrediction(
                    match.secondPlayerPrediction,
                    "-right-4"
                  )}
                </div>
              </div>
            ))}
          {isAdditionalVisible(duelState?.roundMatches) ? (
            <div className="hidden">
              <hr />
              {duelState?.roundMatches
                ?.filter((match) => match.IsAdditional)
                .map((match) => (
                  <div
                    key={match.MatchId}
                    className="flex items-center justify-around px-0 py-2"
                  >
                    <div className="justify-start w-8 flex items-center flex-[0_1_90px] relative">
                      {getPlayerPrediction(
                        match.firstPlayerPrediction,
                        "-left-4"
                      )}
                    </div>
                    <div className="flex justify-center items-center flex-[0_1_80px]">
                      <img
                        src={`/flags/${match.FirstTeam}.png`}
                        alt=""
                        className="h-8 w-8 rounded-full mr-2 object-cover"
                      />
                      <span className="hidden md:block">{match.FirstTeam}</span>
                    </div>
                    <div
                      className={`text-center font-bold w-24 ${
                        match.Calculated ? "text-primary" : "text-red"
                      }`}
                    >
                      {match.FirstScore}-{match.SecondScore}
                    </div>
                    <div className="flex justify-center items-center flex-[0_1_80px]">
                      <span className="md:block hidden">
                        {match.SecondTeam}
                      </span>

                      <img
                        src={`/flags/${match.SecondTeam}.png`}
                        alt=""
                        className="h-8 w-8 rounded-full mr-2 object-cover"
                      />
                    </div>
                    <div className="justify-end w-8 flex items-center flex-[0_1_90px] relative">
                      {getPlayerPrediction(
                        match.secondPlayerPrediction,
                        "-right-4"
                      )}
                    </div>
                  </div>
                ))}
              <div className="text-center font-bold mt-2 text-primary">
                {getPenaltiesScore(duelState?.roundMatches)}
              </div>
            </div>
          ) : (
            <></>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions className="from-primary to-primaryLight bg-gradient-to-b !justify-center !items-center">
        <Button variant="outlined" onClick={handleClose}>
          <CloseIcon />
          &nbsp;Закрити
        </Button>
      </DialogActions>
    </Dialog>
  );
}
